import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { fetchAuthOpts, formatApiError } from "@/lib/api";
import { fmtPrice } from "@/lib/totals";
import {
  Search, ChevronLeft, ChevronRight, Download, Mail, MessageCircle,
  Trash2, Plus, FileText, Undo2, Pencil, ArrowRight, Clock,
} from "lucide-react";
import { toast } from "sonner";
import NewDocDialog from "@/components/billing/NewDocDialog";
import { KIND_LABEL, KIND_PILL, KIND_ICON, formatDateTime } from "@/components/billing/constants";
import InfoRow from "@/components/billing/InfoRow";
import Dialog from "@/components/billing/Dialog";
import DocView from "@/components/billing/DocView";
import useUnsavedChanges, { UnsavedChangesDialog } from "@/hooks/useUnsavedChanges";
import UnsavedDot from "@/components/UnsavedDot";
import { EmailPreviewBlock } from "@/components/email/EmailI18n";

export default function Billing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [filter, setFilter] = useState(searchParams.get("kind") || "all");
  const [q, setQ] = useState("");
  const [docs, setDocs] = useState([]);
  const [selectedId, setSelectedId] = useState(searchParams.get("doc") || null);
  const [selected, setSelected] = useState(null);
  const [neighbors, setNeighbors] = useState({ next: null, previous: null });
  const [payMethods, setPayMethods] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [sendDialog, setSendDialog] = useState(false);
  const [sendEmail, setSendEmail] = useState("");
  const [sendLang, setSendLang] = useState("");
  // Email vivo del cliente cargado vía /api/customers/{id} cada vez que cambia
  // el documento seleccionado. Se usa para prerellenar el diálogo de envío y
  // detectar cuando el cliente NO tiene email configurado.
  const [customerEmail, setCustomerEmail] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Iter 94 — Edición manual de la ficha de cliente EN el documento
  // (Facturas, Albaranes, Abonos). NO modifica la ficha original.
  const [editCustDialog, setEditCustDialog] = useState(false);
  const [custForm, setCustForm] = useState({
    name: "", tax_id: "", address: "", postal_code: "",
    city: "", province: "", country: "", email: "", phone: "",
  });
  const [savingCust, setSavingCust] = useState(false);
  // Iter 101 — Modal de selección cuando el documento tiene varias reparaciones
  const [goRepairDialog, setGoRepairDialog] = useState(false);
  // Iter 107 — Modal de facturas pendientes de cobro
  const [pendingDialog, setPendingDialog] = useState(false);
  const [pendingList, setPendingList] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [payMethodFor, setPayMethodFor] = useState({}); // {docId: method}

  // edits
  const [items, setItems] = useState([]);
  const [parts, setParts] = useState([]);
  const [obs, setObs] = useState("");
  const [internal, setInternal] = useState("");
  const [pm, setPm] = useState("");
  // Lookup vivo de reparaciones para mostrar Equipo|Modelo|Nº Serie
  // bajo cada línea (read-only, fuente única = ficha de reparación).
  const [repairLookup, setRepairLookup] = useState({});

  // Iter 82 — Detección de cambios sin guardar en el documento en edición.
  // Comparamos las secciones editables con el snapshot actual de `selected`.
  const isDirty = useMemo(() => {
    if (!selected) return false;
    const norm = (arr) => (arr || []).map((it) => ({
      description: it.description ?? "",
      quantity: Number(it.quantity ?? 0),
      unit_price: Number(it.unit_price ?? 0),
      vat_rate: Number(it.vat_rate ?? 0),
      product_id: it.product_id ?? null,
      repair_id: it.repair_id ?? null,
    }));
    const base = {
      items: norm(selected.items),
      obs: selected.observations || "",
      internal: selected.internal_notes || "",
      pm: selected.payment_method || "Pendiente",
    };
    const now = {
      items: norm(items),
      obs, internal, pm,
    };
    return JSON.stringify(base) !== JSON.stringify(now);
  }, [selected, items, obs, internal, pm]);

  // Cambio de documento con guardia interno (no dispara useBlocker porque no
  // hay navegación de router). Guardamos el id destino y mostramos el diálogo.
  const [pendingSwitch, setPendingSwitch] = useState(null); // id | 'null-clear'
  const [switchSaving, setSwitchSaving] = useState(false);
  const requestSwitch = (nextId) => {
    if (!isDirty || nextId === selectedId) {
      setSelectedId(nextId);
      return;
    }
    setPendingSwitch(nextId ?? "null-clear");
  };

  const loadList = async () => {
    const r = await api.get("/documents", { params: { kind: filter, q, limit: 200 } });
    setDocs(r.data);
    if (!selectedId && r.data.length) setSelectedId(r.data[0].id);
  };

  const loadSelected = async () => {
    if (!selectedId) {
      setSelected(null);
      setCustomerEmail("");
      return;
    }
    try {
      const [d, n, rl] = await Promise.all([
        api.get(`/documents/${selectedId}`),
        api.get(`/documents/${selectedId}/neighbors`),
        api.get(`/documents/${selectedId}/repair-lookup`).catch(() => ({ data: {} })),
      ]);
      setSelected(d.data);
      setNeighbors(n.data);
      setItems(d.data.items || []);
      setObs(d.data.observations || "");
      setInternal(d.data.internal_notes || "");
      setPm(d.data.payment_method || "Pendiente");
      setRepairLookup(rl.data || {});
      // Cargar email vivo del cliente (no usar customer_tax_snapshot que es
      // foto del momento de emisión, puede estar obsoleto si el cliente lo
      // actualizó después). Fallback al snapshot si la petición falla.
      try {
        if (d.data.customer_id) {
          const c = await api.get(`/customers/${d.data.customer_id}`);
          setCustomerEmail((c.data?.email || "").trim());
        } else {
          setCustomerEmail("");
        }
      } catch {
        setCustomerEmail((d.data.customer_tax_snapshot?.email || "").trim());
      }
    } catch (err) {
      toast.error(formatApiError(err));
      setSelected(null);
      setCustomerEmail("");
    }
  };

  useEffect(() => {
    loadList();
    api.get("/settings").then((r) => setPayMethods(r.data.payment_methods || []));
    api.get("/parts").then((r) => setParts(r.data));
  }, [filter, q]);

  useEffect(() => {
    if (selectedId) {
      setSearchParams({ doc: selectedId }, { replace: true });
    }
    loadSelected();
  }, [selectedId]);

  const saveEdits = async () => {
    try {
      const r = await api.put(`/documents/${selectedId}`, {
        observations: obs, internal_notes: internal, payment_method: pm,
        items,
      });
      setSelected(r.data);
      setItems(r.data.items || []);
      toast.success("Documento actualizado");
      loadList();
      return true;
    } catch (err) {
      toast.error(formatApiError(err));
      return false;
    }
  };

  // Iter 82 — Guardia para cierre de pestaña / navegación de router.
  const unsaved = useUnsavedChanges({ dirty: isDirty, onSaveAndExit: saveEdits });

  // Iter 101 — Reparaciones asociadas al documento. Compone la lista única
  // usando `doc.repair_id` (a nivel documento) + `item.repair_id_ref` (por línea),
  // resuelta con `repairLookup` (código + equipo + serial). Deduplica por id.
  const relatedRepairs = useMemo(() => {
    if (!selected) return [];
    const ids = new Set();
    if (selected.repair_id) ids.add(selected.repair_id);
    (selected.items || []).forEach((it) => {
      if (it?.repair_id_ref) ids.add(it.repair_id_ref);
    });
    return Array.from(ids)
      .map((id) => {
        const info = repairLookup[id] || {};
        return {
          id,
          code: info.code || selected.repair_code || "",
          device_type: info.device_type || "",
          model: info.model || "",
          serial: info.serial || "",
        };
      })
      .filter((r) => r.id);
  }, [selected, repairLookup]);

  // Iter 101 — Click del botón "IR": una reparación → navega directo;
  // varias → abre modal para elegir; ninguna → toast informativo.
  const goToRepair = () => {
    if (relatedRepairs.length === 0) {
      toast.info("Este documento no tiene reparación asociada.");
      return;
    }
    if (relatedRepairs.length === 1) {
      navigate(`/repairs/${relatedRepairs[0].id}`);
      return;
    }
    setGoRepairDialog(true);
  };

  const createCreditNote = async () => {
    try {
      const r = await api.post(`/documents/${selectedId}/credit-note`);
      toast.success(`Abono ${r.data.code} creado`);
      setSelectedId(r.data.id);
      loadList();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadPdf = async () => {
    try {
      const r = await fetch(`${api.defaults.baseURL}/documents/${selectedId}/pdf`, fetchAuthOpts());
      if (!r.ok) {
        toast.error("Error generando PDF");
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${selected?.code || "documento"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      return blob;
    } catch {
      toast.error("Error descargando PDF");
    }
  };

  const sendByEmail = async () => {
    if (!sendEmail) {
      toast.error("Indica un email destinatario");
      return;
    }
    try {
      const r = await api.post(`/documents/${selectedId}/send-pdf`, { to: sendEmail, language_code: sendLang || null });
      toast.success(r.data.message);
      setSendDialog(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendWhatsApp = async () => {
    const tax = selected?.customer_tax_snapshot || {};
    const phone = (tax.phone || "").trim();
    let realPhone = phone;
    if (!realPhone) {
      try {
        const c = await api.get(`/customers/${selected.customer_id}`);
        realPhone = (c.data.phone || "").trim();
      } catch (err) {
        console.warn("[Billing] no se pudo cargar teléfono del cliente:", err);
      }
    }
    if (!realPhone) {
      toast.error("Este cliente no tiene teléfono guardado.");
      return;
    }
    await downloadPdf();
    const cleanPhone = realPhone.replace(/[^0-9+]/g, "");
    const msg = encodeURIComponent(
      `Hola ${selected.customer_name},\nAdjunto ${KIND_LABEL[selected.kind]} ${selected.code} por un total de ${fmtPrice(selected.total)}. Gracias.`
    );
    window.open(`https://wa.me/${cleanPhone}?text=${msg}`, "_blank");
    toast.info("PDF descargado. Adjúntalo en WhatsApp.");
  };

  const doDelete = async () => {
    try {
      // Si el documento tiene repair_id y es factura/albarán, preguntar si devolver stock
      let returnStock = false;
      if (selected && selected.repair_id && (selected.kind === "invoice" || selected.kind === "delivery_note")) {
        const hasLinkedItems = (selected.items || []).some((it) => it.product_id);
        if (hasLinkedItems) {
          returnStock = window.confirm("Este documento descontó stock al generarse. ¿Devolver los repuestos al inventario?\n\n• Aceptar = SÍ, repongo el stock\n• Cancelar = NO");
        }
      }
      await api.delete(`/documents/${selectedId}`, { params: { return_stock: returnStock } });
      toast.success(returnStock ? "Documento eliminado y stock devuelto" : "Documento eliminado");
      setConfirmDelete(false);
      setSelectedId(null);
      loadList();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Iter 94 — Abrir modal precargando el snapshot actual del documento.
  const openEditCustomer = () => {
    if (!selected) return;
    const snap = selected.customer_tax_snapshot || {};
    setCustForm({
      name: snap.name || selected.customer_name || "",
      tax_id: snap.tax_id || "",
      address: snap.address || "",
      postal_code: snap.postal_code || "",
      city: snap.city || "",
      province: snap.province || "",
      country: snap.country || "",
      email: snap.email || "",
      phone: snap.phone || "",
    });
    setEditCustDialog(true);
  };

  // Iter 107 — Abre el modal de facturas pendientes y carga los datos.
  const openPending = async () => {
    setPendingDialog(true);
    setPendingLoading(true);
    try {
      // Solo facturas: kind=invoice. Filtramos pendientes en cliente (payment_method
      // == "Pendiente" o vacío) porque el backend ya devuelve todos los tipos.
      const r = await api.get("/documents", { params: { kind: "invoice", limit: 500 } });
      const all = Array.isArray(r.data) ? r.data : (r.data.items || []);
      const pending = all.filter((d) => {
        const pm = (d.payment_method || "").trim().toLowerCase();
        return pm === "" || pm === "pendiente";
      });
      setPendingList(pending);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPendingLoading(false);
    }
  };

  const markAsPaid = async (docId, method) => {
    const m = (method || "").trim();
    if (!m || m.toLowerCase() === "pendiente") {
      toast.error("Selecciona un método de pago válido");
      return;
    }
    try {
      await api.put(`/documents/${docId}`, { payment_method: m });
      toast.success("Factura marcada como cobrada");
      // Refresh both list and selected doc if applicable
      setPendingList((prev) => prev.filter((d) => d.id !== docId));
      if (selectedId === docId) {
        const r = await api.get(`/documents/${docId}`);
        setSelected(r.data);
      }
      loadList();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exportPendingXlsx = () => {
    const url = `${api.defaults.baseURL}/reports/export/billing?date_from=&date_to=`;
    window.open(url, "_blank");
  };

  const openDocFromPending = (id) => {
    setPendingDialog(false);
    setSelectedId(id);
    setSearchParams({ doc: id });
  };

  const saveCustomerSnapshot = async () => {
    if (savingCust) return;
    setSavingCust(true);
    try {
      const r = await api.patch(`/documents/${selectedId}/customer-snapshot`, custForm);
      setSelected(r.data);
      // También refrescamos el email vivo cacheado para el diálogo de envío,
      // porque el usuario acaba de sobrescribir el email del documento.
      setCustomerEmail((r.data.customer_tax_snapshot?.email || "").trim());
      toast.success("Datos del cliente actualizados en este documento");
      setEditCustDialog(false);
      loadList(); // refrescar nombre en la lista
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingCust(false);
    }
  };

  const kindIcon = (k) => {
    const I = KIND_ICON[k] || FileText;
    return <I size={11} />;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="px-6 lg:px-8 pt-6 lg:pt-8 pb-4 border-b border-dashed border-[#D5D7CE]" style={{ background: "var(--bg)" }}>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[.14em] text-[#3F5222] font-semibold">
              <span className="text-[#8A9098]">//</span>&nbsp;&nbsp;Documentos contables
            </div>
            <h1 className="font-display text-3xl md:text-[38px] font-black mt-2 tracking-tight">Facturación</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={goToRepair}
              disabled={!selected || relatedRepairs.length === 0}
              data-testid="billing-goto-repair-btn"
              title={
                !selected
                  ? "Selecciona un documento"
                  : relatedRepairs.length === 0
                  ? "Este documento no tiene reparación asociada"
                  : relatedRepairs.length === 1
                  ? `Ir a ${relatedRepairs[0].code || "la reparación asociada"}`
                  : `Elegir entre ${relatedRepairs.length} reparaciones asociadas`
              }
              className="ds-btn"
            >
              <ArrowRight size={14} /> IR
              {relatedRepairs.length > 1 && (
                <span className="text-[10px] font-mono border border-current px-1 rounded">{relatedRepairs.length}</span>
              )}
            </button>
            <button
              onClick={openPending}
              data-testid="billing-pending-btn"
              title="Ver facturas pendientes de cobro"
              className="ds-btn warn"
            >
              <Clock size={14} /> Pendientes
            </button>
            <Link
              to="/billing/expenses"
              data-testid="billing-expenses-link"
              className="ds-btn"
            >
              <FileText size={14} /> Gastos
            </Link>
            <button
              onClick={openEditCustomer}
              disabled={!selected}
              data-testid="billing-edit-customer-btn"
              title={selected ? "Editar los datos del cliente solo en este documento" : "Selecciona un documento para editar sus datos de cliente"}
              className="ds-btn"
            >
              <Pencil size={14} /> Editar
            </button>
            <button
              onClick={() => setShowCreate(true)}
              data-testid="billing-new"
              className="ds-btn primary"
            >
              <Plus size={14} /> Nuevo documento
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A9098]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nº, cliente, NIF, reparación..."
              data-testid="billing-search"
              className="ds-input pl-9"
            />
          </div>
          <div className="ds-pager">
            {["all", "invoice", "delivery_note", "credit_note"].map((k) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                data-testid={`billing-filter-${k}`}
                className={filter === k ? "!bg-[#0F1210] !text-white" : ""}
              >
                {k === "all" ? "Todos" : KIND_LABEL[k] + "s"}
              </button>
            ))}
          </div>
          <span className="text-[10px] font-mono text-[#8A9098]">{docs.length} docs</span>
        </div>
      </header>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[260px_1fr_300px]">
        {/* Left: list */}
        <aside className="border-r border-[#E5E5E0] bg-white overflow-y-auto max-h-[calc(100vh-180px)]">
          {docs.length === 0 ? (
            <div className="p-6 text-xs text-[#525252] text-center">
              Sin documentos. {filter !== "all" && "Cambia el filtro o crea uno nuevo."}
            </div>
          ) : (
            docs.map((d) => (
              <button
                key={d.id}
                onClick={() => requestSwitch(d.id)}
                data-testid={`billing-item-${d.code}`}
                className={`w-full text-left px-3 py-2.5 border-b border-[#E5E5E0] hover:bg-[#F7F7F4] transition-colors ${
                  selectedId === d.id ? "bg-[#F0F4E8] border-l-2 border-l-[#6E8B3D]" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono uppercase border ${KIND_PILL[d.kind]}`}>
                    {kindIcon(d.kind)}
                    {KIND_LABEL[d.kind]}
                  </span>
                  <span className="font-mono text-[10px] text-[#525252]">
                    {new Date(d.created_at).toLocaleDateString("es")}
                  </span>
                </div>
                <div className="font-mono text-xs font-semibold mt-1">{d.code}</div>
                <div className="text-xs text-[#525252] truncate">{d.customer_name}</div>
                <div className="font-mono text-[10px] mt-1">
                  {fmtPrice((d.kind === "credit_note" ? -1 : 1) * (d.total || 0))}
                </div>
              </button>
            ))
          )}
        </aside>

        {/* Center: document view */}
        <main className="overflow-y-auto max-h-[calc(100vh-180px)] bg-[#F7F7F4]">
          {!selected ? (
            <div className="p-12 text-center text-sm text-[#525252]">
              Selecciona un documento para verlo.
            </div>
          ) : (
            <div className="p-6 lg:p-8">
              {/* Nav arrows */}
              <div className="flex items-center justify-between mb-4">
                <button
                  disabled={!neighbors.previous}
                  onClick={() => neighbors.previous && requestSwitch(neighbors.previous)}
                  data-testid="billing-prev"
                  className="inline-flex items-center gap-2 border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-1.5 text-xs disabled:opacity-30"
                >
                  <ChevronLeft size={12} /> Anterior
                </button>
                <button
                  disabled={!neighbors.next}
                  onClick={() => neighbors.next && requestSwitch(neighbors.next)}
                  data-testid="billing-next"
                  className="inline-flex items-center gap-2 border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-1.5 text-xs disabled:opacity-30"
                >
                  Siguiente <ChevronRight size={12} />
                </button>
              </div>

              <DocView
                doc={selected}
                items={items} setItems={setItems}
                parts={parts}
                obs={obs} setObs={setObs}
                internal={internal} setInternal={setInternal}
                pm={pm} setPm={setPm}
                payMethods={payMethods}
                repairLookup={repairLookup}
                onSave={saveEdits}
                isDirty={isDirty}
              />
            </div>
          )}
        </main>

        {/* Right: info & actions */}
        <aside className="border-l border-[#E5E5E0] bg-white p-4 space-y-4 overflow-y-auto max-h-[calc(100vh-180px)]">
          {selected ? (
            <>
              <div>
                <div className="font-display font-bold text-sm mb-2">Información</div>
                <div className="space-y-1.5 text-xs">
                  <InfoRow label="Documento" value={KIND_LABEL[selected.kind]} />
                  <InfoRow label="Número" value={selected.code} mono />
                  <InfoRow label="Fecha" value={formatDateTime(selected.created_at)} mono />
                  <InfoRow label="Creado por" value={selected.created_by || "—"} />
                  <InfoRow label="Cliente" value={selected.customer_name} />
                  <InfoRow label="Total" value={fmtPrice((selected.kind === "credit_note" ? -1 : 1) * (selected.total || 0))} mono strong />
                  <InfoRow label="Forma de pago" value={selected.payment_method || "—"} />
                  <InfoRow label="Fecha de cobro" value={selected.paid_at ? new Date(selected.paid_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }) : "—"} mono />
                  {selected.repair_code && (
                    <InfoRow label="Reparación" value={
                      <Link to={`/repairs/${selected.repair_id}`} className="text-[#6E8B3D] hover:underline">
                        {selected.repair_code}
                      </Link>
                    } />
                  )}
                  {selected.source_invoice_code && (
                    <InfoRow label="Factura origen" value={selected.source_invoice_code} mono />
                  )}
                </div>
              </div>

              <div className="space-y-2 pt-3 border-t border-[#E5E5E0]">
                <button
                  onClick={downloadPdf}
                  data-testid="billing-download"
                  className="w-full inline-flex items-center justify-center gap-2 border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-2 text-xs font-semibold"
                >
                  <Download size={14} /> Descargar PDF
                </button>
                <button
                  onClick={() => { setSendEmail(customerEmail); setSendLang(""); setSendDialog(true); }}
                  data-testid="billing-email"
                  className="w-full inline-flex items-center justify-center gap-2 border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-2 text-xs font-semibold"
                >
                  <Mail size={14} /> Enviar por email
                </button>
                <button
                  onClick={sendWhatsApp}
                  data-testid="billing-whatsapp"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white px-3 py-2 text-xs font-semibold"
                >
                  <MessageCircle size={14} /> Enviar por WhatsApp
                </button>
                {(selected.kind === "invoice" || selected.kind === "delivery_note") && (
                  <button
                    onClick={createCreditNote}
                    data-testid="billing-credit-note"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-3 py-2 text-xs font-semibold"
                  >
                    <Undo2 size={14} /> Crear abono
                  </button>
                )}
                <button
                  onClick={() => setConfirmDelete(true)}
                  data-testid="billing-delete"
                  className="w-full inline-flex items-center justify-center gap-2 border border-red-300 text-red-700 hover:bg-red-50 px-3 py-2 text-xs font-semibold"
                >
                  <Trash2 size={14} /> Eliminar
                </button>
              </div>
            </>
          ) : (
            <div className="text-xs text-[#525252]">Selecciona un documento.</div>
          )}
        </aside>
      </div>

      {sendDialog && (
        <Dialog onClose={() => setSendDialog(false)} title="Enviar por email">
          {!customerEmail ? (
            <div className="space-y-3">
              <div className="border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="billing-email-no-email-warning">
                Este cliente no tiene una dirección de email configurada.
              </div>
              <Link
                to={`/customers/${selected?.customer_id}`}
                onClick={() => setSendDialog(false)}
                data-testid="billing-email-goto-customer"
                className="block text-center bg-[#111110] hover:bg-black text-white px-3 py-2 text-sm font-semibold"
              >
                Ir a ficha de cliente →
              </Link>
              <button
                onClick={() => setSendDialog(false)}
                className="block w-full border border-[#E5E5E0] hover:border-[#111110] px-3 py-2 text-xs"
              >
                Cancelar
              </button>
            </div>
          ) : (
            <>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                Email destinatario (uno o varios separados por , o ;)
              </label>
              <input
                type="text"
                value={sendEmail}
                onChange={(e) => setSendEmail(e.target.value)}
                placeholder={customerEmail || "email@cliente.com, otro@empresa.com"}
                data-testid="billing-email-to"
                className="mt-1 w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
              />
              <p className="mt-1 text-[10px] text-[#525252]">
                Rellenado automáticamente desde la ficha del cliente. Puedes editarlo antes de enviar.
              </p>
              <div className="mt-3">
                <EmailPreviewBlock
                  templateKey={{ invoice: "invoice_send", credit_note: "credit_note_send", delivery_note: "delivery_note_send" }[selected?.kind] || "invoice_send"}
                  docId={selectedId}
                  language={sendLang}
                  onLanguage={(v) => setSendLang(v)}
                />
              </div>
              <button
                onClick={sendByEmail}
                data-testid="billing-email-send"
                className="mt-3 w-full bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-3 py-2 text-sm font-semibold"
              >
                <Mail size={14} className="inline mr-1" /> Enviar
              </button>
            </>
          )}
        </Dialog>
      )}

      {confirmDelete && (
        <Dialog onClose={() => setConfirmDelete(false)} title="Eliminar documento">
          <p className="text-sm">
            Solo se puede eliminar el último documento de su numeración. ¿Continuar?
          </p>
          <div className="mt-4 flex gap-2">
            <button onClick={() => setConfirmDelete(false)} className="flex-1 border border-[#111110] px-3 py-2 text-sm">
              Cancelar
            </button>
            <button onClick={doDelete} data-testid="billing-delete-confirm" className="flex-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 text-sm font-semibold">
              Eliminar
            </button>
          </div>
        </Dialog>
      )}

      {showCreate && (
        <NewDocDialog onClose={() => setShowCreate(false)} onCreated={(d) => { setShowCreate(false); setSelectedId(d.id); loadList(); }} />
      )}

      {/* Iter 82 — Diálogo de cambios sin guardar (navegación router + cierre pestaña) */}
      {unsaved.dialog}

      {/* Iter 107 — Modal de facturas pendientes de cobro */}
      {pendingDialog && (
        <Dialog onClose={() => setPendingDialog(false)} title="Facturas pendientes de cobro" size="xl">
          <div className="space-y-3" data-testid="billing-pending-modal">
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs text-[#525252]">
                {pendingLoading
                  ? "Cargando..."
                  : `${pendingList.length} factura(s) pendiente(s) de cobro. Los albaranes y abonos no se incluyen.`}
              </div>
              <button
                type="button"
                onClick={exportPendingXlsx}
                data-testid="billing-pending-export"
                className="inline-flex items-center gap-1 border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-1.5 text-xs"
              >
                <Download size={11} /> Exportar
              </button>
            </div>
            <div className="max-h-[60vh] overflow-auto border border-[#E5E5E0]">
              <table className="w-full text-xs">
                <thead className="bg-[#F7F7F4] sticky top-0">
                  <tr className="text-left">
                    <th className="px-2 py-2 font-mono uppercase tracking-widest text-[10px]">Nº factura</th>
                    <th className="px-2 py-2 font-mono uppercase tracking-widest text-[10px]">Fecha</th>
                    <th className="px-2 py-2 font-mono uppercase tracking-widest text-[10px]">Cliente</th>
                    <th className="px-2 py-2 font-mono uppercase tracking-widest text-[10px] text-right">Total</th>
                    <th className="px-2 py-2 font-mono uppercase tracking-widest text-[10px] text-right">Cobrado</th>
                    <th className="px-2 py-2 font-mono uppercase tracking-widest text-[10px] text-right">Pendiente</th>
                    <th className="px-2 py-2 font-mono uppercase tracking-widest text-[10px]">Vencimiento</th>
                    <th className="px-2 py-2 font-mono uppercase tracking-widest text-[10px]">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingList.map((d) => {
                    const dt = (d.created_at || "").slice(0, 10);
                    const total = Number(d.total || 0);
                    // Sin pagos parciales por ahora: cobrado=0 · pendiente=total
                    return (
                      <tr key={d.id} className="border-t border-[#E5E5E0] hover:bg-[#F7F7F4]" data-testid={`pending-row-${d.id}`}>
                        <td className="px-2 py-1.5 font-mono">{d.code}</td>
                        <td className="px-2 py-1.5">{dt}</td>
                        <td className="px-2 py-1.5">{d.customer_name}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{fmtPrice(total)}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-[#525252]">0 €</td>
                        <td className="px-2 py-1.5 text-right font-mono font-bold text-amber-700">{fmtPrice(total)}</td>
                        <td className="px-2 py-1.5 text-[#525252]">{d.due_date || "—"}</td>
                        <td className="px-2 py-1.5">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openDocFromPending(d.id)}
                              className="border border-[#111110] hover:bg-[#111110] hover:text-white px-2 py-1 text-[10px]"
                              data-testid={`pending-open-${d.id}`}
                              title="Abrir factura"
                            >
                              Abrir
                            </button>
                            <select
                              value={payMethodFor[d.id] || ""}
                              onChange={(e) => setPayMethodFor({ ...payMethodFor, [d.id]: e.target.value })}
                              className="border border-[#E5E5E0] focus:border-[#6E8B3D] outline-none px-1 py-0.5 text-[10px] rounded-none"
                              data-testid={`pending-method-${d.id}`}
                            >
                              <option value="">Método...</option>
                              {payMethods.filter((m) => (m || "").toLowerCase() !== "pendiente").map((m) => (
                                <option key={m} value={m}>{m}</option>
                              ))}
                            </select>
                            <button
                              onClick={() => markAsPaid(d.id, payMethodFor[d.id] || "")}
                              className="bg-emerald-700 hover:bg-emerald-800 text-white px-2 py-1 text-[10px]"
                              data-testid={`pending-mark-paid-${d.id}`}
                              title="Marcar como cobrada"
                            >
                              Cobrar
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!pendingLoading && pendingList.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center py-6 text-xs text-[#525252]">
                        No hay facturas pendientes de cobro
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-[#525252] italic">
              Estado de cobro: una factura se considera <strong>pendiente</strong> cuando su
              método de pago es &quot;Pendiente&quot; o está vacío. Al asignar un método real (Efectivo,
              Tarjeta, Transferencia, etc.) queda registrada como <strong>cobrada</strong>.
            </p>
          </div>
        </Dialog>
      )}

      {/* Iter 101 — Modal de selección de reparación cuando el documento
          tiene varias reparaciones asociadas. */}
      {goRepairDialog && selected && (
        <Dialog onClose={() => setGoRepairDialog(false)} title={`Reparaciones asociadas · ${selected.code}`}>
          <div className="space-y-2">
            <p className="text-xs text-[#525252]">
              Este documento tiene varias reparaciones vinculadas. Elige a cuál quieres ir:
            </p>
            <div className="border-t border-[#E5E5E0]" data-testid="billing-goto-repair-list">
              {relatedRepairs.map((rep) => (
                <button
                  key={rep.id}
                  onClick={() => {
                    setGoRepairDialog(false);
                    navigate(`/repairs/${rep.id}`);
                  }}
                  data-testid={`billing-goto-repair-item-${rep.id}`}
                  className="w-full text-left flex items-center justify-between gap-3 px-3 py-2 border-b border-[#E5E5E0] hover:bg-[#F0F4E8] group"
                >
                  <div className="flex flex-col">
                    <span className="font-mono text-xs text-[#111110] group-hover:text-[#6E8B3D]">
                      {rep.code || rep.id.slice(0, 8)}
                    </span>
                    <span className="text-xs text-[#525252]">
                      {[rep.device_type, rep.model, rep.serial].filter(Boolean).join(" · ") || "—"}
                    </span>
                  </div>
                  <ArrowRight size={14} className="text-[#525252] group-hover:text-[#6E8B3D]" />
                </button>
              ))}
            </div>
          </div>
        </Dialog>
      )}

      {/* Iter 94 — Modal de edición de la ficha del cliente EN el documento */}
      {editCustDialog && selected && (
        <Dialog onClose={() => !savingCust && setEditCustDialog(false)} title={`Editar datos de cliente · ${selected.code}`}>
          <div className="space-y-3">
            <div className="border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="billing-edit-customer-notice">
              Los cambios afectarán únicamente a este documento (PDF, impresión y email).
              La ficha original del cliente no se modificará.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                ["name", "Nombre / Razón social", "md:col-span-2"],
                ["tax_id", "NIF / CIF"],
                ["phone", "Teléfono"],
                ["email", "Email", "md:col-span-2"],
                ["address", "Dirección", "md:col-span-2"],
                ["postal_code", "Código postal"],
                ["city", "Población"],
                ["province", "Provincia"],
                ["country", "País"],
              ].map(([field, label, extraCls]) => (
                <label key={field} className={`block ${extraCls || ""}`}>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                    {label}
                  </span>
                  <input
                    type={field === "email" ? "email" : "text"}
                    value={custForm[field] || ""}
                    onChange={(e) => setCustForm((f) => ({ ...f, [field]: e.target.value }))}
                    data-testid={`billing-edit-customer-${field}`}
                    className="mt-1 w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
                  />
                </label>
              ))}
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E5E5E0]">
              <button
                onClick={() => setEditCustDialog(false)}
                disabled={savingCust}
                className="border border-[#E5E5E0] hover:border-[#111110] px-3 py-2 text-xs disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={saveCustomerSnapshot}
                disabled={savingCust}
                data-testid="billing-edit-customer-save"
                className="bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-4 py-2 text-xs font-semibold disabled:opacity-50"
              >
                {savingCust ? "Guardando..." : "Guardar cambios"}
              </button>
            </div>
          </div>
        </Dialog>
      )}

      {/* Iter 82 — Diálogo de cambios sin guardar cuando se cambia entre documentos */}
      {pendingSwitch !== null && (
        <UnsavedChangesDialog
          saving={switchSaving}
          onCancel={() => setPendingSwitch(null)}
          onDiscard={() => {
            const target = pendingSwitch === "null-clear" ? null : pendingSwitch;
            setPendingSwitch(null);
            setSelectedId(target);
          }}
          onSaveAndExit={async () => {
            setSwitchSaving(true);
            const ok = await saveEdits();
            setSwitchSaving(false);
            if (ok) {
              const target = pendingSwitch === "null-clear" ? null : pendingSwitch;
              setPendingSwitch(null);
              setSelectedId(target);
            }
          }}
        />
      )}
    </div>
  );
}
